import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

export class ApiHelper {
  private readonly className = "ApiHelper";
  private axiosInstance: AxiosInstance;
  private logger = console;

  constructor(params: { url: string; [key: string]: any }) {
    const { url, ...rest } = params;
    this.axiosInstance = axios.create({
      baseURL: url,
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-cache",
        ...rest,
      },
    });
  }

  private handleResponse<T>(response: AxiosResponse<T>): T {
    return response.data;
  }

  private handleError(error: AxiosError): void {
    const errorMessage = `Error in ${this.className}: ${error.message}`;

    if (error.response) {
      const { status, data: url = "" }: any = error.response;
      this.logger.error(`${errorMessage} - Status: ${status}`);
      if (status === 301) {
        window.location.href = url;
      }
    } else if (error.request) {
      this.logger.error(`${errorMessage} - No response received`);
    } else {
      this.logger.error(`${errorMessage} - Request setup error`);
    }
    // this.logger.error(this.className, `Error config: ${error.config.data}`);
  }

  private async request<T>(
    method: "get" | "post" | "delete" | "put",
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T | { errors: string[] }> {
    try {
      const response = await this.axiosInstance.request<T>({
        method,
        url,
        data,
        ...config,
      });

      return this.handleResponse(response);
    } catch (error: any) {
      this.handleError(error);

      return { errors: [error.message] };
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T | { errors: string[] }> {
    return this.request<T>("get", url, undefined, config);
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T | { errors: string[] }> {
    return this.request<T>("post", url, data, config);
  }

  async delete<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T | { errors: string[] }> {
    return this.request<T>("delete", url, data, config);
  }

  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T | { errors: string[] }> {
    return this.request<T>("put", url, data, config);
  }
}
